import { FC, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { typeScale } from '@/shared/styles/typography'

type FigureProps = {
  src?: string
  alt?: string
}

/**
 * 手順の図版。markdown の ![キャプション](/guide/xxx.png) から生成される。
 *
 * 外側の帯 (bg.subtle) の幅を揃えて縦のリズムを作り、画像自体は素材の寸法のまま置く。
 * 罫線は最後の手段なので、白地の図版が本文に溶ける問題も地色で解く。
 * 背の高い素材は maxHeight で頭を揃え、原寸はクリックでその場で見せる (もう一度押すと戻る)。
 * モーダルにしないのは、覆うと手順の文が隠れ、閉じる操作を別に用意することになるため
 * (まもる君 /guide の Figure と同じ判断。design-book 03-patterns/modals.md §1)。
 */
const FIGURE_MAX_HEIGHT = 400

export const Figure: FC<FigureProps> = ({ src, alt }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!src) return null

  return (
    <Box component="figure" sx={{ m: 0, my: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: colorTokens.bg.subtle,
          borderRadius: `${RADIUS.md}px`,
          p: 2,
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt ?? ''}
          loading="lazy"
          onClick={() => setIsExpanded((v) => !v)}
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: isExpanded ? 'none' : FIGURE_MAX_HEIGHT,
            width: 'auto',
            height: 'auto',
            borderRadius: `${RADIUS.sm}px`,
            cursor: isExpanded ? 'zoom-out' : 'zoom-in',
          }}
        />
      </Box>
      {alt && (
        <Typography
          component="figcaption"
          sx={{
            ...typeScale.bodySm,
            color: colorTokens.fg.muted,
            mt: 1,
          }}
        >
          {alt}
        </Typography>
      )}
    </Box>
  )
}
